import React, { useCallback, useMemo } from 'react'
import classNames from 'classnames'
import { Input, Select } from '@st-operations/uikit/src/v2'

import ValidatedForm from '../validated-form/index.jsx'
import { useEventFeedsContext } from '../../../context/index.jsx'
import CustomCheckbox from '_components/custom-checkbox/index.jsx'
import pretty from '@st-operations/uikit/lib/pretty.js'

const body = document.querySelector('body')

const DeliveryFrequencyForm = ({
  requiredErrorFrequency,
  requiredErrorFrequencyFrom,
  requiredErrorFrequencyTo,
}) => {
  const {
    data: { deliveries, type },
    schema,
    dispatch,
    ACTIONS,
  } = useEventFeedsContext()
  const delivery = deliveries[0] ?? {}

  const isUup = type === 'uup'

  const frequencyOptions = useMemo(() => {
    const valuesEnum = schema?.deliveries?.['Frequency Configuration']?.frequency?.enum
    const sampleOptions = valuesEnum
      .filter((option) => option === 'one-time')
      ?.map((option) => ({
        label: option ? pretty(option) : option,
        value: option,
      }))
    const liveFeedsOptions = valuesEnum
      .filter((option) => option !== 'one-time')
      ?.map((option) => ({
        label: option ? pretty(option) : option,
        value: option,
      }))
    return [
      { label: 'Live data feed', options: liveFeedsOptions },
      { label: 'Sample data', options: sampleOptions },
    ]
  }, [schema])

  const uupModeOptions = useMemo(() => ['daily', 'aggregated'], [])

  const uupFrequencyOptions = useCallback(
    (isSample, mode) => (!isSample && mode === 'aggregated' ? ['monthly'] : ['daily', 'monthly']),
    []
  )

  const onDeliverySelectChange = (option, name, value) => {
    dispatch({
      type: ACTIONS.UPDATE_DELIVERY,
      payload: {
        [name]: value,
      },
    })
  }

  const onDeliveryInputChange = (value, name) => {
    dispatch({
      type: ACTIONS.UPDATE_DELIVERY,
      payload: {
        [name]: value,
      },
    })
  }

  const showDate = delivery.frequency === 'one-time' || delivery.isSample === true

  const fromToDates = [
    {
      label: 'Date From',
      required: requiredErrorFrequencyFrom,
      validationClassName: 'delivery-content__required--frequency',
      show: showDate,
      // eslint-disable-next-line react/display-name
      render: () => (
        <DateField
          label="From"
          description="Includes the specified events starting from the chosen date"
          name="sampleStartDate"
          value={delivery.sampleStartDate}
          onChange={onDeliveryInputChange}
          max={delivery.sampleEndDate}
          requiredError={requiredErrorFrequencyFrom}
        />
      ),
    },
    {
      label: 'Date To',
      required: requiredErrorFrequencyTo,
      validationClassName: 'delivery-content__required--frequency',
      show: showDate,
      // eslint-disable-next-line react/display-name
      render: () => (
        <DateField
          label="To"
          description="Includes the specified events up to the chosen end date"
          name="sampleEndDate"
          value={delivery.sampleEndDate}
          onChange={onDeliveryInputChange}
          requiredError={requiredErrorFrequencyTo}
        />
      ),
    },
  ]

  return (
    <ValidatedForm
      inputs={
        isUup
          ? [
              {
                label: 'Is Sample',
                render: () => (
                  <>
                    <span className="delivery-content__content-label">Is Sample</span>
                    <CustomCheckbox
                      className="ml-4"
                      checked={delivery.isSample || false}
                      onChange={(e) =>
                        onDeliverySelectChange(undefined, 'isSample', e.target.checked)
                      }
                    />
                  </>
                ),
              },
              {
                label: 'Mode',
                render: () => (
                  <>
                    <span className="delivery-content__content-label">Mode</span>
                    <Select
                      className={classNames(
                        'delivery-content__content-select',
                        'delivery-content__content-no-margin',
                        'ml-10'
                      )}
                      name="mode"
                      value={delivery.mode || 'daily'}
                      options={uupModeOptions}
                      onChange={onDeliverySelectChange}
                      isSearchable
                      menuPortalTarget={body}
                      menuPosition="fixed"
                      styles={{
                        menuPortal: (provided) => ({
                          ...provided,
                          zIndex: 9999,
                          maxHeight: 200,
                        }),
                      }}
                      useReactSelect
                      pretty
                    />
                  </>
                ),
              },
              {
                label: 'Frequency',
                render: () => (
                  <>
                    <span className="delivery-content__content-label">Frequency</span>
                    <Select
                      className={classNames(
                        'delivery-content__content-select',
                        'delivery-content__content-no-margin'
                      )}
                      name="frequency"
                      value={delivery.frequency}
                      options={uupFrequencyOptions(delivery.isSample, delivery.mode)}
                      onChange={onDeliverySelectChange}
                      isSearchable
                      menuPortalTarget={body}
                      menuPosition="fixed"
                      styles={{
                        menuPortal: (provided) => ({
                          ...provided,
                          zIndex: 9999,
                          maxHeight: 200,
                        }),
                      }}
                      useReactSelect
                      pretty
                    />
                  </>
                ),
              },
              ...fromToDates,
            ]
          : [
              {
                label: 'Frequency',
                required: requiredErrorFrequency,
                validationClassName: 'delivery-content__required--frequency',
                render: () => (
                  <>
                    <span className="delivery-content__content-label">Frequency</span>
                    <div className="delivery-content__content-select">
                      <Select
                        className={classNames('!m-0', 'delivery-content__content-no-margin', {
                          'delivery-content__required-field': requiredErrorFrequency,
                        })}
                        name="frequency"
                        value={frequencyOptions
                          .map((group) => group.options)
                          .flat()
                          .find((option) => option.value === delivery.frequency)}
                        options={frequencyOptions}
                        onChange={onDeliverySelectChange}
                        isSearchable
                        menuPortalTarget={body}
                        menuPosition="fixed"
                        styles={{
                          menuPortal: (provided) => ({
                            ...provided,
                            zIndex: 9999,
                            maxHeight: 200,
                          }),
                        }}
                        useReactSelect
                      />
                      <div className="collapsible-section__description mt-2">
                        Select Daily/Hourly for a live data feed or One-time to for a sample from a
                        specific period
                      </div>
                    </div>
                  </>
                ),
              },
              ...fromToDates,
            ]
      }
    />
  )
}

const DateField = ({ label, description, name, value, onChange, max, requiredError }) => {
  return (
    <>
      <span
        className={classNames(
          'delivery-content__content-label',
          'delivery-content__content-label--frequency-onetime'
        )}
      >
        {label}
      </span>
      <div className="delivery-content__content-select">
        <Input
          className={classNames('!m-0', {
            'delivery-content__required-field': requiredError,
          })}
          type="date"
          name={name}
          placeholder="YYYY-MM-DD"
          value={value}
          onChange={onChange}
          max={max}
          onFocus={(e) => {
            e.target.showPicker()
          }}
        />
        <If condition={description}>
          <div className="collapsible-section__description mt-2">{description}</div>
        </If>
      </div>
    </>
  )
}

export default DeliveryFrequencyForm
